import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

export const fetchReports = createAsyncThunk(
  "researchReports/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      const endpoint = '/reports';
      const queryParams = typeof params === 'object' && params !== null ? params : {};
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL || '/api'}${endpoint}`, { params: queryParams });

      if (!Array.isArray(data)) {
        throw new Error("Invalid data format for reports");
      }

      const normalizePlayers = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(v => v && v.trim());
        if (typeof value === 'string') return value.split('\n').filter(v => v.trim());
        return [];
      };

      const normalized = data.map((r) => ({
        id: r.id,
        category: r.category || r.title,
        title: r.title,
        Industry: r.industry || r.Industry,
        RegionsCovered: r.region || r.RegionsCovered,
        MajorPlayers: normalizePlayers(r.majorPlayers || r.major_players || r.MajorPlayers),
        subCategory: r.subCategory || r.sub_category || "General",
        Author: r.author || r.Author,
        PublicationDate: r.publicationDate || r.publication_date || r.PublicationDate,
        ProductCode: r.productCode || r.product_code || r.ProductCode,
        StudyPeriod: r.forecastPeriod || r.forecast_period || r.StudyPeriod || "N/A",
        CAGR: r.cagr || r.CAGR || "XX%",
        MarketSize: r.marketSize || r.market_size || "N/A",
        marketSizeForecast: r.marketSizeForecast || r.market_size_forecast || "N/A",
        marketSize: r.marketSize || r.market_size || "N/A", // Lowercase for LatestReportsBar
        Pages: r.pages || r.no_of_pages || r.Pages || "N/A",
        format: r.format || r.Format || "PDF + XLS + PPT",
        subtitle: r.subtitle || r.Subtitle,
        Description: r.description || r.Description,
        Price: r.price || r.Price,
        price: r.price || r.Price,
        SampleReportUrl: r.sampleReportUrl || r.sample_report_url || r.SampleReportUrl,
        FullReportUrl: r.fullReportUrl || r.full_report_url || r.FullReportUrl,
        slug: r.slug,
        status: r.status,
        pricingOptions: (() => {
          const raw = r.pricing_options || r.pricingOptions;
          if (!raw) return null;
          if (typeof raw === 'string') {
            try {
              return JSON.parse(raw);
            } catch (e) {
              return null;
            }
          }
          return raw;
        })(),
        createdAt: r.createdAt || r.created_at,
        updatedAt: r.updatedAt || r.updated_at,
      }));

      return normalized;

    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Failed to fetch reports");
    }
  },
  {
    condition: (params, { getState }) => {
      const { researchReports } = getState();
      if (researchReports.loading) {
        return false;
      }
      if (params?.force) {
        return true;
      }
      if (researchReports.fetched) {
        return false;
      }
    }
  }
);

export const deleteReport = createAsyncThunk(
  "researchReports/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL || '/api'}/admin/reports/delete/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to delete report");
    }
  }
);

const researchReportSlice = createSlice({
  name: "researchReports",
  initialState: {
    reports: [],
    loading: false,
    error: null,
    fetched: false,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload;
        state.fetched = true;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteReport.fulfilled, (state, action) => {
        state.reports = state.reports.filter((r) => r.id !== action.payload);
      });
  },
});

export default researchReportSlice.reducer;
